import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import WelcomeDialog from "@/components/WelcomeDialog";
// iter60 — Code splitting: pages are code-split into individual JS
// chunks fetched on demand. The initial bundle now ships only the
// shell (Router + Layout + Auth + Home critical path). Every other
// route resolves lazily the first time it's visited, then the
// browser cache holds it. Home is *eager* because it's the landing
// page (users tolerate zero loading spinner there); Login is also
// eager so unauthenticated redirects don't flash a spinner.
import Home from "@/pages/Home";
import Login from "@/pages/Login";
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const Publish = lazy(() => import("@/pages/Publish"));
const Profile = lazy(() => import("@/pages/Profile"));
const MyAgenda = lazy(() => import("@/pages/MyAgenda"));
const MapView = lazy(() => import("@/pages/MapView"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ShareQR = lazy(() => import("@/pages/Share"));
const Charter = lazy(() => import("@/pages/Charter"));
const QuiSommesNous = lazy(() => import("@/pages/QuiSommesNous"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Contact = lazy(() => import("@/pages/Contact"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const PastEvents = lazy(() => import("@/pages/PastEvents"));
const PovList = lazy(() => import("@/pages/PovList"));
const PovDetail = lazy(() => import("@/pages/PovDetail"));
const PovEditor = lazy(() => import("@/pages/PovEditor"));
const ProPlans = lazy(() => import("@/pages/ProPlans"));
const Donate = lazy(() => import("@/pages/Donate"));
const SubscribeWidget = lazy(() => import("@/pages/Subscribe"));
const Billing = lazy(() => import("@/pages/Billing"));
const AdminPayments = lazy(() => import("@/pages/AdminPayments"));
const AdminShareDigest = lazy(() => import("@/pages/AdminShareDigest"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminCVF = lazy(() => import("@/pages/AdminCVF"));
const ChatList = lazy(() => import("@/pages/ChatList"));
const ChatRoom = lazy(() => import("@/pages/ChatRoom"));
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import PushOptinPrompt from "@/components/PushOptinPrompt";
import ScrollToTop from "@/components/ScrollToTop";
import PlausibleTracker from "@/components/PlausibleTracker";
import NewVersionBanner from "@/components/NewVersionBanner";
import "@/App.css";

// iter60 — Fallback while a lazy chunk is loading. Kept minimal (no
// full-screen overlay) so the layout doesn't jump when the chunk
// resolves in under a second on a warm cache.
function RouteFallback() {
    return (
        <div className="p-10 text-center text-sm text-muted-foreground"
             data-testid="route-loading">
            Chargement…
        </div>
    );
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-10 text-center">Chargement...</div>;
    if (!user) {
        // PWA Web Share Target: if the user is mid-share (?shared=…) but
        // not authenticated, the OAuth/login redirect would drop the
        // share param. Stash it in sessionStorage so /publish can pick
        // the flyer back up after login (read in Publish.jsx).
        try {
            const params = new URLSearchParams(loc.search);
            const shared = params.get("shared");
            if (shared) {
                sessionStorage.setItem("pending_shared_flyer", shared);
            }
        } catch (_) { /* sessionStorage disabled (Safari private) */ }
        return <Navigate to="/login" replace />;
    }
    return children;
}

/**
 * Handles the Google OAuth return from auth.emergentagent.com.
 * The provider appends `#session_id=...` to whichever redirect URL
 * we passed (which is `window.location.origin + '/'`). We detect that
 * fragment synchronously during render, exchange it for our own JWT
 * via POST /api/auth/google-exchange, then strip the fragment from
 * the URL and reload the auth context. Using useRef ensures the
 * exchange runs ONCE even under React 18 StrictMode.
 */
function GoogleAuthCallback({ onDone }) {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        (async () => {
            try {
                const hash = window.location.hash || "";
                const m = hash.match(/session_id=([^&]+)/);
                if (!m) { onDone?.(); return; }
                const sessionId = m[1];
                const r = await api.post("/auth/google-exchange", { session_id: sessionId });
                const { token, user } = r.data || {};
                if (!token || !user) throw new Error("missing token in response");
                localStorage.setItem("token", token);
                setUser(user);
                // Wipe fragment and route home.
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
                onDone?.();
                navigate("/", { replace: true });
            } catch (e) {
                setError(e?.response?.data?.detail || e?.message || "Erreur de connexion Google");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
                <div className="max-w-md text-center space-y-3">
                    <h1 className="text-lg font-semibold">Connexion Google impossible</h1>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <button onClick={() => navigate("/login", { replace: true })}
                            className="mt-3 px-4 py-2 rounded bg-primary text-primary-foreground"
                            data-testid="google-auth-retry">
                        Retour à la connexion
                    </button>
                </div>
            </div>
        );
    }
    return <div className="p-10 text-center">Connexion…</div>;
}

function AppRouter() {
    const location = useLocation();
    const [pending, setPending] = useState(
        typeof window !== "undefined" && window.location.hash?.includes("session_id=")
    );
    // Synchronous check during render — required to beat the AuthProvider's
    // /auth/me check that would otherwise race the OAuth exchange.
    if (pending) {
        return <GoogleAuthCallback onDone={() => setPending(false)} />;
    }
    // Re-route any leftover fragment after navigation.
    if (location.hash?.includes("session_id=")) {
        return <GoogleAuthCallback onDone={() => setPending(false)} />;
    }
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/*" element={<Shell />} />
            </Routes>
        </Suspense>
    );
}

function Shell() {
    const { loading, user } = useAuth();
    if (loading) return <div className="p-10 text-center">Chargement...</div>;
    return (
        <Layout>
            {user && <WelcomeDialog />}
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/events/:id" element={<EventDetail />} />
                    <Route path="/charter" element={<Charter />} />
                    <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />
                    <Route path="/about" element={<QuiSommesNous />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/share" element={<ShareQR />} />
                    <Route path="/map" element={<MapView />} />
                    <Route path="/publish" element={<Protected><Publish /></Protected>} />
                    <Route path="/agenda" element={<Protected><MyAgenda /></Protected>} />
                    <Route path="/profile" element={<Protected><Profile /></Protected>} />
                    <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
                    <Route path="/past-events" element={<Protected><PastEvents /></Protected>} />
                    <Route path="/pov" element={<PovList />} />
                    <Route path="/pov/new" element={<Protected><PovEditor /></Protected>} />
                    <Route path="/pov/:id/edit" element={<Protected><PovEditor /></Protected>} />
                    <Route path="/pov/:id" element={<PovDetail />} />
                    <Route path="/pro" element={<ProPlans />} />
                    <Route path="/don" element={<Donate />} />
                    <Route path="/subscribe" element={<Protected><SubscribeWidget /></Protected>} />
                    <Route path="/billing" element={<Protected><Billing /></Protected>} />
                    <Route path="/admin/payments" element={<Protected><AdminPayments /></Protected>} />
                    <Route path="/admin/share-digest" element={<Protected><AdminShareDigest /></Protected>} />
                    <Route path="/admin/analytics" element={<Protected><AdminAnalytics /></Protected>} />
                    <Route path="/admin/cvf" element={<Protected><AdminCVF /></Protected>} />
                    <Route path="/chat" element={<Protected><ChatList /></Protected>} />
                    <Route path="/chat/:id" element={<Protected><ChatRoom /></Protected>} />
                    <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </Layout>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <PlausibleTracker />
                        <NewVersionBanner />
                        <AppRouter />
                        <Toaster richColors position="top-center" />
                        <PwaInstallPrompt />
                        <PushOptinPrompt />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}
